import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiEdit,
  FiTrash2,
  FiArrowLeft,
  FiMail,
  FiPhone,
  FiMapPin,
  FiCalendar,
  FiUser,
  FiHeart,
  FiAlertCircle,
  FiPause,
  FiPlay,
  FiX,
  FiTrendingUp,
} from 'react-icons/fi';
import { QRCodeSVG } from 'qrcode.react';
import memberService from '../services/member.service';
import membershipService from '../services/membership.service';
import S3Image from '../components/S3Image';
import { formatDate, formatDateLong, calculateAge } from '../utils/dateUtils';
import { confirmDelete, confirmUnfreeze, showActiveMembershipError } from '../utils/swal';
import DateInput from '../components/DateInput';

const MemberDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const hasFetched = useRef(false);
  const currentId = useRef(id);

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState([]);
  const [loadingMemberships, setLoadingMemberships] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [newStartDate, setNewStartDate] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeOptions, setUpgradeOptions] = useState({});
  const [loadingUpgrade, setLoadingUpgrade] = useState(false);

  useEffect(() => {
    // Reset flag if ID changes (navigating to different member)
    if (currentId.current !== id) {
      hasFetched.current = false;
      currentId.current = id;
    }

    // Prevent duplicate fetches on mount
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchMember();
      fetchMemberships();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchMember = async () => {
    try {
      setLoading(true);
      const response = await memberService.getMemberById(id);
      setMember(response.data);
    } catch (error) {
      toast.error('Failed to fetch member details');
      navigate('/members');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const confirmed = await confirmDelete(`${member.first_name} ${member.last_name}`, 'member');
    if (confirmed) {
      try {
        await memberService.deleteMember(id);
        toast.success('Member deleted successfully');
        navigate('/members');
      } catch (error) {
        // Check if error is due to active membership
        if (error.response?.status === 400 && error.response?.data?.message?.includes('active membership')) {
          await showActiveMembershipError();
        } else {
          toast.error(error.response?.data?.message || 'Failed to delete member');
        }
      }
    }
  };

  const handleFreeze = () => {
    navigate(`/members/${id}/freeze`);
  };

  const handleUnfreeze = async () => {
    const confirmed = await confirmUnfreeze(`${member.first_name} ${member.last_name}`);
    if (confirmed) {
      try {
        await memberService.unfreezeMembership(id);
        toast.success('Membership unfrozen successfully');
        fetchMember();
      } catch (error) {
        toast.error('Failed to unfreeze membership');
      }
    }
  };

  const fetchMemberships = async () => {
    try {
      setLoadingMemberships(true);
      const response = await membershipService.getAllMemberships({ member_id: id, limit: 100 });
      setMemberships(response.data?.memberships || []);
    } catch (error) {
      console.error('Failed to fetch memberships:', error);
    } finally {
      setLoadingMemberships(false);
    }
  };

  const canUpdateStartDate = (membership) => {
    return membership.status === 'active';
  };

  const canUpgradePlan = (membership) => {
    return membership.status === 'active';
  };

  const handleUpdateStartDate = (membership) => {
    setSelectedMembership(membership);
    setNewStartDate(membership.start_date);
    setShowUpdateModal(true);
  };

  const submitUpdateStartDate = async () => {
    if (!newStartDate) {
      toast.error('Please select a start date');
      return;
    }

    try {
      await membershipService.updateStartDate(selectedMembership.id, newStartDate);
      toast.success('Start date updated successfully');
      setShowUpdateModal(false);
      fetchMemberships();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update start date';
      toast.error(errorMessage);
    }
  };

  const handleUpgradePlan = async (membership) => {
    try {
      setLoadingUpgrade(true);
      const response = await membershipService.getUpgradeOptions(id);
      setUpgradeOptions(response.data || {});
      setSelectedMembership(membership);
      setShowUpgradeModal(true);
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to load upgrade options';
      toast.error(errorMessage);
    } finally {
      setLoadingUpgrade(false);
    }
  };

  const handleSelectUpgrade = (plan) => {
    console.log('=== Upgrade Plan Selection ===');
    console.log('Selected Plan:', plan);
    console.log('Member ID:', id);
    console.log('Current Membership ID:', selectedMembership?.id);
    console.log('Current Plan Price:', upgradeOptions.current_membership?.plan_price);

    navigate('/membership/purchase', {
      state: {
        memberId: id,
        plan: plan,
        isUpgrade: true,
        currentMembershipId: selectedMembership.id,
        currentPlanPrice: upgradeOptions.current_membership?.plan_price || 0,  // Current plan's price
        currentPlanName: upgradeOptions.current_membership?.plan_name || ''
      }
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      expired: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      frozen: 'bg-yellow-100 text-yellow-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const styles = {
      paid: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      partial: 'bg-orange-100 text-orange-800',
    };
    return (
      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${styles[paymentStatus] || 'bg-gray-100 text-gray-800'}`}>
        {paymentStatus?.charAt(0).toUpperCase() + paymentStatus?.slice(1)}
      </span>
    );
  };

  const hasActiveMembership = () => {
    return memberships.some(membership => membership.status === 'active');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!member) {
    return <div>Member not found</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <button
          onClick={() => navigate('/members')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <FiArrowLeft className="mr-2" />
          Back to Members
        </button>
        <div className="flex space-x-2">
          <button
            onClick={() => navigate(`/members/${id}/edit`)}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <FiEdit className="mr-2" />
            Edit
          </button>
          {member.is_frozen ? (
            <button
              onClick={handleUnfreeze}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FiPlay className="mr-2" />
              Unfreeze
            </button>
          ) : (
            <button
              onClick={handleFreeze}
              disabled={member.membership_status !== 'active'}
              title={member.membership_status !== 'active' ? 'Member must have an active membership to freeze' : 'Freeze membership'}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                member.membership_status !== 'active'
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-yellow-600 text-white hover:bg-yellow-700'
              }`}
            >
              <FiPause className="mr-2" />
              Freeze
            </button>
          )}
          <button
            onClick={handleDelete}
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            <FiTrash2 className="mr-2" />
            Delete
          </button>
        </div>
      </div>

      {/* Member Profile Card */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-32"></div>
        <div className="px-6 pb-6">
          <div className="flex flex-col md:flex-row md:items-end md:space-x-6">
            {/* Profile Photo */}
            <div className="-mt-16 mb-4 md:mb-0">
              <S3Image
                src={member.photo}
                alt={`${member.first_name} ${member.last_name}`}
                className="w-32 h-32 rounded-full border-4 border-white object-cover"
                fallback={
                  <div className="w-32 h-32 rounded-full border-4 border-white bg-gray-200 flex items-center justify-center text-4xl font-bold text-gray-500">
                    {member.first_name?.[0]}{member.last_name?.[0]}
                  </div>
                }
              />
            </div>

            {/* Member Info */}
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-gray-900">
                {member.first_name} {member.last_name}
              </h1>
              <p className="text-gray-600 mt-1">Member ID: {member.member_id}</p>
              <div className="flex flex-wrap gap-2 mt-3">
                {member.is_frozen ? (
                  <span className="px-3 py-1 text-sm font-semibold rounded-full bg-yellow-100 text-yellow-800">
                    Frozen
                  </span>
                ) : member.is_active ? (
                  <span className="px-3 py-1 text-sm font-semibold rounded-full bg-green-100 text-green-800">
                    Active
                  </span>
                ) : (
                  <span className="px-3 py-1 text-sm font-semibold rounded-full bg-red-100 text-red-800">
                    Inactive
                  </span>
                )}
                {member.membership_status ? (
                  <span className="px-3 py-1 text-sm font-semibold rounded-full bg-blue-100 text-blue-800">
                    {member.plan_name || member.membership_status}
                  </span>
                ) : (
                  <span className="px-3 py-1 text-sm font-semibold rounded-full bg-gray-100 text-gray-800">
                    No Plan
                  </span>
                )}
              </div>
            </div>

            {/* QR Code */}
            {member.qr_code && (
              <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                <QRCodeSVG value={`MEMBER:${member.member_id}`} size={100} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Contact Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h2>
          <div className="space-y-3">
            <div className="flex items-center">
              <FiMail className="text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="text-gray-900">{member.email || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center">
              <FiPhone className="text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="text-gray-900">{member.phone || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center">
              <FiMapPin className="text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Address</p>
                <p className="text-gray-900">
                  {member.address ? (
                    <>
                      {member.address}
                      {(member.city || member.state || member.pincode) && (
                        <>
                          <br />
                          {[member.city, member.state, member.pincode].filter(Boolean).join(', ')}
                        </>
                      )}
                    </>
                  ) : (
                    'N/A'
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Personal Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Personal Information</h2>
          <div className="space-y-3">
            <div className="flex items-center">
              <FiCalendar className="text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Date of Birth</p>
                <p className="text-gray-900">
                  {formatDate(member.date_of_birth)} ({calculateAge(member.date_of_birth)} years)
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <FiUser className="text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Gender</p>
                <p className="text-gray-900 capitalize">{member.gender || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center">
              <FiHeart className="text-gray-400 mr-3" />
              <div>
                <p className="text-sm text-gray-500">Blood Group</p>
                <p className="text-gray-900">{member.blood_group || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Membership Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Membership Information</h2>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500">Branch</p>
              <p className="text-gray-900 font-medium">{member.branch_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Joining Date</p>
              <p className="text-gray-900">{formatDate(member.joining_date)}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Current Plan</p>
              <p className="text-gray-900">{member.plan_name || 'No active plan'}</p>
            </div>
            {member.membership_end_date && (
              <div>
                <p className="text-sm text-gray-500">Membership Expires</p>
                <p className="text-gray-900">{formatDate(member.membership_end_date)}</p>
              </div>
            )}
            {member.is_frozen && (
              <>
                <div>
                  <p className="text-sm text-gray-500">Freeze Start Date</p>
                  <p className="text-gray-900">{formatDate(member.freeze_start_date)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Freeze End Date</p>
                  <p className="text-gray-900">{formatDate(member.freeze_end_date)}</p>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Emergency Contact */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Emergency Contact</h2>
          <div className="space-y-3">
            <div>
              <p className="text-sm text-gray-500">Contact Name</p>
              <p className="text-gray-900">{member.emergency_contact_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Contact Phone</p>
              <p className="text-gray-900">{member.emergency_contact_phone || 'N/A'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Medical Information */}
      {member.medical_conditions && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
            <FiAlertCircle className="mr-2 text-red-500" />
            Medical Information
          </h2>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-gray-700">{member.medical_conditions}</p>
          </div>
        </div>
      )}

      {/* Trainer Information */}
      {member.trainer && (
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Assigned Trainer</h2>
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-semibold">
              {member.trainer.first_name?.[0]}{member.trainer.last_name?.[0]}
            </div>
            <div>
              <p className="font-medium text-gray-900">
                {member.trainer.first_name} {member.trainer.last_name}
              </p>
              <p className="text-sm text-gray-500">{member.trainer.specialization}</p>
              <p className="text-sm text-gray-500">{member.trainer.phone}</p>
            </div>
          </div>
        </div>
      )}

      {/* Membership History */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">Membership History</h2>
          <div className="relative group">
            <button
              onClick={() => navigate('/membership/plans', { state: { memberId: id } })}
              disabled={hasActiveMembership()}
              className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                hasActiveMembership()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Purchase New Plan
            </button>
            {hasActiveMembership() && (
              <div className="absolute right-0 mt-2 w-64 bg-gray-800 text-white text-xs rounded-lg p-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                Cannot purchase a new plan while an active membership exists
              </div>
            )}
          </div>
        </div>

        {loadingMemberships ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        ) : memberships.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No membership history found</p>
            <button
              onClick={() => navigate('/membership/plans')}
              className="mt-4 text-blue-600 hover:text-blue-700"
            >
              Browse Membership Plans
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {memberships.map((membership) => (
                  <tr key={membership.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{membership.plan_name}</div>
                      <div className="text-xs text-gray-500">{membership.duration_days} days</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(membership.start_date)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      {formatDate(membership.end_date)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                      ₹{parseFloat(membership.amount_paid).toFixed(2)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getStatusBadge(membership.status)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {getPaymentStatusBadge(membership.payment_status)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-3">
                        {canUpdateStartDate(membership) && (
                          <button
                            onClick={() => handleUpdateStartDate(membership)}
                            className="text-blue-600 hover:text-blue-900 flex items-center"
                          >
                            <FiCalendar className="mr-1" size={16} />
                            Update Date
                          </button>
                        )}
                        {canUpgradePlan(membership) && (
                          <button
                            onClick={() => handleUpgradePlan(membership)}
                            className="text-purple-600 hover:text-purple-900 flex items-center"
                            disabled={loadingUpgrade}
                          >
                            <FiTrendingUp className="mr-1" size={16} />
                            Upgrade
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Update Start Date Modal */}
      {showUpdateModal && selectedMembership && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Update Start Date</h3>
              <button
                onClick={() => setShowUpdateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-4">
                Plan: {selectedMembership.plan_name}
              </p>

              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Start Date
              </label>
              <DateInput
                name="new_start_date"
                value={newStartDate}
                onChange={(value) => setNewStartDate(value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="DD/MM/YYYY"
                required
              />
              <p className="mt-1 text-xs text-gray-500">Enter date as: DD/MM/YYYY (e.g., 28/12/2025)</p>
              <p className="text-xs text-gray-500 mt-2">
                Current start date: {formatDate(selectedMembership.start_date)}
              </p>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowUpdateModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={submitUpdateStartDate}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Update
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Plan Modal */}
      {showUpgradeModal && selectedMembership && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold text-gray-900">Upgrade Membership Plan</h3>
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600 mb-2">
                Member: {member?.first_name} {member?.last_name}
              </p>
              <p className="text-sm font-medium text-gray-700 mb-1">
                Current Plan: {upgradeOptions.current_membership?.plan_name}
              </p>
              <p className="text-xs text-gray-500">
                Price: ₹{upgradeOptions.current_membership?.plan_price} |
                Expires: {formatDate(upgradeOptions.current_membership?.end_date)}
              </p>
            </div>

            {upgradeOptions.available_upgrades && upgradeOptions.available_upgrades.length > 0 ? (
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Available Upgrades:</h4>
                {upgradeOptions.available_upgrades.map((plan) => (
                  <div
                    key={plan.id}
                    className="border border-gray-200 rounded-lg p-4 hover:border-purple-500 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => handleSelectUpgrade(plan)}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h5 className="font-bold text-gray-900">{plan.name}</h5>
                        <p className="text-sm text-gray-600">{plan.duration_days} days</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-purple-600">
                          +₹{plan.upgrade_details.upgrade_cost}
                        </p>
                        <p className="text-xs text-gray-500">Upgrade Cost</p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-2">{plan.description}</p>
                    <div className="flex justify-between items-center text-xs text-gray-500">
                      <span>New Plan Price: ₹{plan.final_price}</span>
                      <span className="text-purple-600 font-medium">Click to upgrade</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <p>No higher-tier plans available for upgrade</p>
              </div>
            )}

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowUpgradeModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberDetail;
